<template>
  <b-modal
    ref="generateUserModal"
    centered
    size="md"
    hide-header-close
    scrollable
    no-close-on-esc
    footer-bg-variant=""
    :title="generateUserModalTitle"
    :header-text-variant="userIsGenerarated ? 'success' : 'default'"
    @hide="onHideAttempt"
    @hidden="
      unselectReseller(),
      clearGeneratedUserData(),
      clearGenerateUserPayload(),
      clearGenerateUserErrors()
    ">

    <b-overlay
      :show="resellersResponses('generateUserReseller')"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>

      <template v-if="!userIsGenerarated">
        <b-form @submit.stop.prevent>
          <div class="font-weight-bold mb-2">Expiration</div>
          <b-tabs
            small
            content-class="border-left border-right border-bottom rounded-bottom p-4"
            class="mb-6">

            <b-tab
              title="Specify Date"
              active>
              <b-form-group
                id="expiry-datepicker"
                label-for="expiration-date-input"
                class="mb-0">
                <b-form-datepicker
                  id="expiration-date-input"
                  name="expiration-date-input"
                  v-model="generateUserPayload.expiry"
                  hide-header
                  start-weekday="1"
                  aria-describedby="expiration-date-feedback"
                  :min="tomorrowDate"
                  :date-format-options="{ day: 'numeric', month: 'numeric', year: 'numeric' }"
                  :state="getValidationState('days')"
                  @input="syncInputs($event, 'expiry')" />
                <b-form-invalid-feedback
                  v-for="(error, index) in generateUserError('days')"
                  :key="`${error.field}-${index}`">
                  {{ error.message }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-tab>

            <b-tab
              title="Specify Days">
              <b-form-group
                id="days-user"
                label-for="days-input"
                class="mb-0">
                <b-form-input
                  id="days-input"
                  name="days-input"
                  type="number"
                  min="1"
                  onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  onwheel="this.blur()"
                  v-model.number="generateUserPayload.days"
                  :state="getValidationState('days')"
                  aria-describedby="days-feedback"
                  placeholder="30 (days)"
                  @input="syncInputs($event, 'days')" />
                <b-form-invalid-feedback
                  v-for="(error, index) in generateUserError('days')"
                  :key="`${error.field}-${index}`">
                  {{ error.message }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-tab>
            
          </b-tabs>

          <b-form-group
            id="devices-user"
            label="Devices"
            label-for="devices-input">
            <b-form-input
              id="devices-input"
              name="devices-input"
              type="number"
              min="1"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
              onwheel="this.blur()"
              v-model.number="generateUserPayload.devices"
              :state="getValidationState('devices')"
              aria-describedby="devices-feedback"
              placeholder="5000" />
            <b-form-invalid-feedback
              v-for="(error, index) in generateUserError('devices')"
              :key="`${error.field}-${index}`">
              {{ error.message }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-form>
      </template>

      <template v-else>
        <b-form-group>
          <label for="userCode-input">User Code</label>
          <b-input-group>
            <b-form-input
              v-model="generatedUserData.code"
              id="userCode-input"
              readonly
              class="flex-grow-1 mr-auto" />
            <b-input-group-append>
              <b-button
                id="copyUserCodeBtn"
                variant="outline-primary"
                data-clipboard-target="#userCode-input"
                class="ml-2">
                <i class="fa fa-copy fa-sm mr-1"></i>
                <span>Copy</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-form-group>
          <label for="userToken-input">Access Token</label>
          <b-input-group>
            <b-form-input
              v-model="generatedUserData.token"
              id="userToken-input"
              readonly
              class="flex-grow-1 mr-auto" />
            <b-input-group-append>
              <b-button
                id="copyUserTokenBtn"
                variant="outline-primary"
                data-clipboard-target="#userToken-input"
                class="ml-2">
                <i class="fa fa-copy fa-sm mr-1"></i>
                <span>Copy</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <div class="font-weight-bold mb-2">User Details</div>
        <b-list-group class="mb-6">
          <b-list-group-item class="d-flex align-items-center">
            Status
            <span class="ml-auto font-size-xs">
              <b-badge :variant="generatedUserStatusBadge(generatedUserData).variant">
                <i :class="`fa fa-sm fa-${generatedUserStatusBadge(generatedUserData).icon} text-white`"></i>
                {{ generatedUserStatusBadge(generatedUserData).text }}
              </b-badge>
            </span>
          </b-list-group-item>
          <b-list-group-item class="d-flex align-items-center">
            Devices
            <span class="ml-auto">{{ generatedUserData.devices }}</span>
          </b-list-group-item>
          <b-list-group-item class="d-flex align-items-center">
            Created
            <span class="ml-auto">{{ generatedUserData.created | moment("DD.MM.YYYY, HH:mm") }}</span>
          </b-list-group-item>
          <b-list-group-item class="d-flex align-items-center">
            Expire at
            <span class="ml-auto">{{ generatedUserData.expiry | moment("DD.MM.YYYY, HH:mm") }}</span>
          </b-list-group-item>
        </b-list-group>
      </template>

      <reseller-details-plate />

    </b-overlay>

    <template #modal-footer>
      <div class="d-flex w-100">
        <b-button
          v-b-tooltip.hover.right
          id="cancelModal"
          :title="!userIsGenerarated ? 'Are you sure? Unsaved data will be lost.' : ''"
          variant="outline-secondary"
          size="md"
          :disabled="resellersResponses('generateUserReseller')"
          @click="hideModal('generateUserModal')">
          Close
        </b-button>
        <b-button
          v-if="!userIsGenerarated"
          v-wave
          variant="primary"
          size="md"
          class="ml-auto"
          :disabled="resellersResponses('generateUserReseller')"
          @click="generateUserReseller">
          Submit
        </b-button>
        <b-button
          v-else
          variant="primary"
          size="md"
          class="ml-auto"
          @click="$router.push({ name: 'UsersList' }).catch(() => {})">
          View all users
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  // actions
  GET_RESELLER,
  GENERATE_USER_RESELLER,

  // mutations
  SELECT_RESELLER,
  UNSELECT_RESELLER,
  CLEAR_GENERATED_USER_DATA,
  CLEAR_GENERATE_USER_PAYLOAD,
  CLEAR_GENERATE_USER_ERRORS,
} from '@/core/services/store/resellers.module'
import {
  onHideAttempt,
  showModal,
  hideModal,
  initClipboard,
  destroyClipboard,
  showToast,
} from '@/core/utils'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import ResellerDetailsPlate from './ResellerDetailsPlate.vue'

export default {
  name: 'ModalGenerateUser',
  components: {
    ResellerDetailsPlate,
  },
  data() {
    return {
      clipboard: null,

      onHideAttempt,
      showModal,
      hideModal,
      initClipboard,
      destroyClipboard,
      showToast,
    }
  },
  computed: {
    ...mapGetters([
      'selectedReseller',
      'selectedResellerId',
      'resellerStatusBadge',
      'resellersResponses',

      'generateUserPayload',
      'generateUserError',
      'generateUserErrors',
      'generatedUserData',
      'userIsGenerarated',
      'generateUserModalTitle',
      'generatedUserStatusBadge',
    ]),

    tomorrowDate() {
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    },
  },
  mounted() {
    eventBus.$on('openModal', type => { this.showModal(type) })

    this.initClipboard('copyUserCodeBtn')
    this.initClipboard('copyUserTokenBtn')
  },
  destroyed() {
    eventBus.$off('openModal')

    this.destroyClipboard()
    this.unselectReseller()
    this.clearGeneratedUserData()
    this.clearGenerateUserPayload()
  },
  methods: {
    generateUserReseller() {
      this.$store
        .dispatch(GENERATE_USER_RESELLER, {
          reseller_id: this.selectedReseller.id
        })
        .then(() => {
          this.$store
            .dispatch(GET_RESELLER, { id: this.selectedReseller.id })
            .then(data => {
              this.selectReseller(data)
            })
        })
    },

    selectReseller(reseller) {
      this.$store.commit(SELECT_RESELLER, reseller)
    },

    unselectReseller() {
      this.$store.commit(UNSELECT_RESELLER)
    },

    syncInputs(event, type) {
      if (type === 'expiry') {
        const startDate = new Date()
        const endDate = new Date(event)
        const timeDifference = endDate - startDate
        const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
        this.generateUserPayload.days = daysDifference + 1
      }
      else if (type === 'days') {
        const num = Number(event)
        if (isNaN(num) || num < 1) {
          this.generateUserPayload.days = 1
          document.querySelector('#days-input').value = 1
        }
        const endDate = new Date()
        endDate.setDate(endDate.getDate() + num)
        this.generateUserPayload.expiry = endDate.toISOString().split('T')[0]
      }
    },

    clearGeneratedUserData() {
      this.$store.commit(CLEAR_GENERATED_USER_DATA)
    },

    clearGenerateUserPayload() {
      this.$store.commit(CLEAR_GENERATE_USER_PAYLOAD)
    },

    clearGenerateUserErrors() {
      this.$store.commit(CLEAR_GENERATE_USER_ERRORS)
    },

    getValidationState(field) {
      return this.generateUserErrors.some(err => err.field == field) ? false : null
    },
  }
}
</script>
